import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Users, Flame, UserCircle,
  HandHeart, MessageSquare, Info,
  History as HistoryIcon,
  Activity, ShieldAlert, MessagesSquare, UserCog, ListChecks, Video, Mail, Radar, Share2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTermsModal } from '../contexts/TermsModalContext';
import { adminListCloudinaryCleanupFailures } from '../lib/api';

// Custom praying-hands icon — matches lucide stroke style
const PrayingHands = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11 3 C 9.5 5, 7.5 9, 7 14 C 6.7 17, 8 19, 9.5 20 L 11.5 20 Z" />
    <path d="M13 3 C 14.5 5, 16.5 9, 17 14 C 17.3 17, 16 19, 14.5 20 L 12.5 20 Z" />
    <line x1="12" y1="3.5" x2="12" y2="20" />
  </svg>
);

const NavItem = ({ icon: Icon, label, to, active, onClick, badge }) => {
  const badgeEl = badge != null && badge > 0 ? (
    <span
      data-testid={`nav-badge-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-rose-500 text-white tabular-nums"
    >
      {badge > 99 ? '99+' : badge}
    </span>
  ) : null;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`flex items-center gap-5 h-10 px-3 mx-1 w-[calc(100%-0.5rem)] rounded-lg text-sm text-left hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${active ? 'bg-zinc-200 dark:bg-zinc-700/80 font-medium' : ''}`}
      >
        <Icon size={20} />
        <span className="truncate">{label}</span>
        {badgeEl}
      </button>
    );
  }
  return (
    <Link
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-5 h-10 px-3 mx-1 rounded-lg text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${active ? 'bg-zinc-200 dark:bg-zinc-700/80 font-medium' : ''}`}
    >
      <Icon size={20} />
      <span className="truncate">{label}</span>
      {badgeEl}
    </Link>
  );
};

const MiniItem = ({ icon: Icon, label, to, active, onClick, badge }) => {
  const dot = badge != null && badge > 0 ? (
    <span
      aria-label={`${badge} pending`}
      className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center rounded-full text-[9px] font-semibold bg-rose-500 text-white tabular-nums leading-none"
    >
      {badge > 9 ? '9+' : badge}
    </span>
  ) : null;
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`mini-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        className={`relative flex flex-col items-center justify-center gap-1 py-3 px-1 w-full rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${active ? 'bg-zinc-200 dark:bg-zinc-700/80' : ''}`}
      >
        {dot}
        <Icon size={22} />
        <span className="text-[10px] leading-tight text-center">{label}</span>
      </button>
    );
  }
  return (
    <Link
      to={to}
      data-testid={`mini-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`relative flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${active ? 'bg-zinc-200 dark:bg-zinc-700/80' : ''}`}
    >
      {dot}
      <Icon size={22} />
      <span className="text-[10px] leading-tight text-center">{label}</span>
    </Link>
  );
};

/**
 * Sidebar menu items — single source of truth so the mini (collapsed) and
 * expanded views can never drift out of sync. The "You" separator has been
 * removed per product feedback; every item shows in both modes now.
 */
const menu = (path, currentUserId) => [
  { key: 'home',      icon: Home,         label: 'Home',            to: '/',                          active: path === '/' },
  { key: 'circles',   icon: Users,        label: 'Joined Circles',  to: '/circles',                   active: path === '/circles' },
  ...(currentUserId
    ? [{ key: 'my-circle', icon: UserCircle, label: 'My Circle',   to: `/circle/${currentUserId}`,   active: path === `/circle/${currentUserId}` }]
    : []),
  { key: 'yours',     icon: PrayingHands, label: 'My Prayers',      to: '/feed/clips',                active: path === '/feed/clips' },
  { key: 'candles',   icon: Flame,        label: 'My Lit Candles',  to: '/feed/liked',                active: path === '/feed/liked' },
  { key: 'history',   icon: HistoryIcon,  label: 'History',         to: '/history',                   active: path === '/history' },
  { key: 'help',      icon: HandHeart,    label: 'Gifts',           to: '/help',                      active: path === '/help' },
  { key: 'feedback',  icon: MessageSquare,label: 'Send feedback',   to: '/feedback',                  active: path === '/feedback' },
  { key: 'about',     icon: Info,         label: 'About',           to: '/about',                     active: path === '/about' },
];

// Admin-only menu items — rendered as a distinct group at the bottom of
// the sidebar. Only surfaced when the current viewer has `role === 'admin'`.
// The `badge` field is a numeric count (or null) that will show up as a
// small red pill on the item — currently used to surface unresolved
// Cloudinary cleanup failures.
const adminMenu = (path, badges = {}) => [
  { key: 'admin-users',    icon: UserCog,       label: 'Users',           to: '/admin/users',           active: path === '/admin/users' },
  { key: 'admin-prayers',  icon: ListChecks,    label: 'All Prayers',     to: '/admin/prayers',         active: path === '/admin/prayers' },
  { key: 'admin-video-cleanup', icon: Video,    label: 'Video Cleanup',   to: '/admin/cloudinary-cleanup', active: path === '/admin/cloudinary-cleanup', badge: badges.videoCleanup || 0 },
  { key: 'admin-misuse',   icon: ShieldAlert,   label: 'Misuse Reports',  to: '/admin/misuse-reports',  active: path === '/admin/misuse-reports' },
  { key: 'admin-activity', icon: Activity,      label: 'Activity',        to: '/admin/activity',        active: path === '/admin/activity' },
  { key: 'admin-feedback', icon: MessagesSquare,label: 'Feedback',        to: '/admin/feedback',        active: path === '/admin/feedback' },
  { key: 'admin-emails',   icon: Mail,          label: 'Email Templates', to: '/admin/email-templates', active: path === '/admin/email-templates' },
  { key: 'admin-link-preview', icon: Share2,    label: 'Link Preview',    to: '/admin/link-preview',    active: path === '/admin/link-preview' },
  { key: 'admin-obs',      icon: Radar,         label: 'Observability',   to: '/admin/observability',   active: path === '/admin/observability' },
];

const Sidebar = ({ open, mini }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { openTerms } = useTermsModal();
  const items = menu(location.pathname, user?.user_id);
  const isAdmin = user?.role === 'admin';
  // Poll the Cloudinary cleanup-failure count so the admin sidebar
  // surfaces a live red badge whenever orphan videos need attention.
  // Only fires for admin viewers; refreshed every 60s + on route change.
  const [videoCleanupCount, setVideoCleanupCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) { setVideoCleanupCount(0); return; }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const data = await adminListCloudinaryCleanupFailures();
        if (!cancelled) setVideoCleanupCount(Number(data?.count) || 0);
      } catch { /* silent — sidebar badge is best-effort */ }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAdmin, location.pathname]);
  const adminItems = isAdmin ? adminMenu(location.pathname, { videoCleanup: videoCleanupCount }) : [];

  if (mini) {
    return (
      <aside className="hidden md:flex md:flex-col w-[72px] flex-shrink-0 sticky top-14 h-[calc(100vh-56px)] overflow-y-auto bg-white dark:bg-[#0f0f0f] text-zinc-900 dark:text-white py-1 scrollbar-thin">
        <div className="flex-1">
          {items.map(it => (
            <MiniItem key={it.key} icon={it.icon} label={it.label} to={it.to} active={it.active} onClick={it.onClick} />
          ))}
          {isAdmin && (
            <>
              <div className="mx-3 my-2 border-t border-zinc-200 dark:border-zinc-800" />
              <div
                data-testid="mini-admin-label"
                className="text-[9px] uppercase tracking-wider text-amber-600 dark:text-amber-400 text-center mb-1"
              >Admin</div>
              {adminItems.map(it => (
                <MiniItem key={it.key} icon={it.icon} label={it.label} to={it.to} active={it.active} badge={it.badge} />
              ))}
            </>
          )}
        </div>
        <div
          data-testid="sidebar-copyright-mini"
          className="mt-auto px-1 pb-3 pt-2 text-[10px] text-zinc-500 leading-tight text-center space-y-1"
        >
          <div>© {new Date().getFullYear()} Pray Now Together</div>
          <button
            type="button"
            onClick={openTerms}
            data-testid="sidebar-terms-link-mini"
            className="text-amber-600 dark:text-amber-400 hover:underline underline-offset-2 leading-snug bg-transparent border-0 p-0 cursor-pointer"
          >
            Terms &amp;<br/>Conditions
          </button>
        </div>
      </aside>
    );
  }

  if (!open) return null;

  return (
    <aside className="fixed md:sticky top-14 left-0 z-40 md:z-0 flex flex-col w-60 h-[calc(100vh-56px)] overflow-y-auto bg-white dark:bg-[#0f0f0f] text-zinc-900 dark:text-white py-2 scrollbar-thin">
      <div className="py-1">
        {items.map(it => (
          <NavItem key={it.key} icon={it.icon} label={it.label} to={it.to} active={it.active} onClick={it.onClick} />
        ))}
        {isAdmin && (
          <>
            <div className="mx-3 my-2 border-t border-zinc-200 dark:border-zinc-800" />
            <div
              data-testid="sidebar-admin-label"
              className="px-4 py-1 text-[11px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400"
            >Admin</div>
            {adminItems.map(it => (
              <NavItem key={it.key} icon={it.icon} label={it.label} to={it.to} active={it.active} badge={it.badge} />
            ))}
          </>
        )}
      </div>
      <div className="px-4 py-3 text-xs text-zinc-500 leading-relaxed">
        Pray Now Together<br/>
        A place to share and be prayed for.
      </div>
      <div
        data-testid="sidebar-copyright"
        className="mt-auto px-4 pb-3 pt-2 text-[11px] text-zinc-400 dark:text-zinc-500 leading-tight"
      >
        <div>© {new Date().getFullYear()} Pray Now Together</div>
        <button
          type="button"
          onClick={openTerms}
          data-testid="sidebar-terms-link"
          className="mt-0.5 inline-block text-[11px] text-amber-600 dark:text-amber-400 hover:underline underline-offset-2 font-medium bg-transparent border-0 p-0 cursor-pointer"
        >
          Terms &amp; Conditions
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
